// StudyDost — Plans Page (Professional Redesign)
// frontend/src/pages/PlansPage.js

import React, { useState } from 'react';
import { Check, Zap, Star, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { paymentAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const PLANS = [
  {
    id: 'FREE',
    name: 'Free',
    tagline: 'Shuru karne ke liye',
    price: 0,
    period: '',
    priceDisplay: 'Free',
    features: [
      { text: '5 questions per day', included: true },
      { text: 'Hindi + English', included: true },
      { text: 'WhatsApp + Web App', included: true },
      { text: 'Text & Photo questions', included: true },
      { text: 'Visual diagrams', included: false },
      { text: 'Parent reports', included: false },
      { text: 'Voice questions', included: false },
    ],
    color: '#64748B',
    bg: '#F8FAFC',
    border: '#E2E8F0',
    badge: null,
    highlight: false,
  },
  {
    id: 'MONTHLY',
    name: 'Monthly',
    tagline: 'Regular students ke liye',
    price: 99,
    originalPrice: 199,
    period: '/month',
    priceDisplay: '₹99',
    perDay: '₹3.3/day',
    features: [
      { text: 'Unlimited questions', included: true },
      { text: 'Visual diagrams 🖼️', included: true },
      { text: 'Voice questions 🎤', included: true },
      { text: 'Flashcard revision 📇', included: true },
      { text: 'Parent weekly report', included: true },
      { text: 'Full question history', included: true },
    ],
    color: '#4F46E5',
    bg: '#EEF2FF',
    border: '#C7D2FE',
    badge: null,
    highlight: false,
  },
  {
    id: 'QUARTERLY',
    name: 'Quarterly',
    tagline: 'Sabse Popular — ₹83/mahina',
    price: 249,
    originalPrice: 499,
    period: '/3 months',
    priceDisplay: '₹249',
    perDay: '₹2.7/day',
    features: [
      { text: 'Everything in Monthly', included: true },
      { text: '₹83/month effectively', included: true },
      { text: 'Save 37% vs monthly', included: true },
      { text: 'Best for exam prep', included: true },
      { text: 'Priority answers', included: true },
    ],
    color: '#059669',
    bg: '#ECFDF5',
    border: '#059669',
    badge: '⭐ Most Popular',
    highlight: true,
  },
  {
    id: 'ANNUAL',
    name: 'Annual',
    tagline: 'Full year — ₹42/mahina',
    price: 499,
    originalPrice: 999,
    period: '/year',
    priceDisplay: '₹499',
    perDay: '₹1.4/day',
    features: [
      { text: 'Everything in Quarterly', included: true },
      { text: '₹42/month effectively', included: true },
      { text: 'Save 55% vs monthly', included: true },
      { text: 'Full academic year', included: true },
      { text: 'White-label homework PDF', included: true },
    ],
    color: '#D97706',
    bg: '#FFFBEB',
    border: '#F59E0B',
    badge: 'Save 55%',
    highlight: false,
  },
];

export default function PlansPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);

  const currentPlan = user?.activePlan || user?.plan || 'FREE';

  const handlePurchase = async (planId) => {
    if (planId === 'FREE') return;
    setLoading(planId);
    try {
      const res = await paymentAPI.createOrder(planId);
      window.location.href = res.data.paymentLink;
    } catch {
      toast.error('Payment link mein problem. Dobara try karein.');
    } finally {
      setLoading(null);
    }
  };

  return (
    <div style={styles.page}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerGlow} />
        <div style={styles.headerContent}>
          <div style={styles.headerTag}>💎 Pricing</div>
          <h1 style={styles.headerTitle}>Apna Plan Choose Karein</h1>
          <p style={styles.headerDesc}>
            Shuru karein free mein — jab chahein upgrade karein.<br />
            Koi hidden charge nahi.
          </p>
        </div>
      </div>

      {/* Plans */}
      <div style={styles.plansContainer}>
        {PLANS.map((plan) => {
          const isCurrent = currentPlan === plan.id;
          return (
            <div
              key={plan.id}
              style={{
                ...styles.planCard,
                borderColor: isCurrent ? plan.color : plan.highlight ? plan.color : plan.border,
                borderWidth: isCurrent || plan.highlight ? 2 : 1,
                background: plan.highlight ? '#FFFFFF' : plan.bg,
                boxShadow: plan.highlight
                  ? `0 20px 60px ${plan.color}20`
                  : isCurrent
                  ? `0 8px 24px ${plan.color}15`
                  : '0 2px 12px rgba(0,0,0,0.04)',
              }}
            >
              {/* Badges */}
              {plan.badge && (
                <div style={{ ...styles.badge, background: plan.color }}>
                  {plan.badge}
                </div>
              )}
              {isCurrent && (
                <div style={{ ...styles.badge, background: '#10B981', left: 16, right: 'auto' }}>
                  ✓ Current Plan
                </div>
              )}

              {/* Plan header */}
              <div style={styles.planTop}>
                <div>
                  <div style={{ ...styles.planName, color: plan.color }}>{plan.name}</div>
                  <div style={styles.planTagline}>{plan.tagline}</div>
                </div>
                <div style={styles.priceBlock}>
                  <div style={{ ...styles.priceMain, color: plan.highlight ? plan.color : '#0F172A' }}>
                    {plan.priceDisplay}
                  </div>
                  <div style={styles.pricePeriod}>{plan.period}</div>
                  {plan.perDay && <div style={styles.perDay}>{plan.perDay}</div>}
                </div>
              </div>

              {/* Features */}
              <div style={styles.featureList}>
                {plan.features.map((f, i) => (
                  <div key={i} style={styles.featureRow}>
                    <div style={{
                      ...styles.featureCheck,
                      background: f.included ? plan.color + '15' : '#F1F5F9',
                      color: f.included ? plan.color : '#CBD5E1',
                    }}>
                      {f.included ? <Check size={12} strokeWidth={3} /> : '×'}
                    </div>
                    <span style={{ ...styles.featureText, color: f.included ? '#374151' : '#CBD5E1' }}>
                      {f.text}
                    </span>
                  </div>
                ))}
              </div>

              {/* CTA */}
              {isCurrent ? (
                <div style={styles.currentPlanBtn}>✓ Yeh aapka current plan hai</div>
              ) : plan.id === 'FREE' ? (
                <button style={{ ...styles.ctaBtn, background: '#F1F5F9', color: '#64748B' }}>
                  Already Free
                </button>
              ) : (
                <button
                  style={{
                    ...styles.ctaBtn,
                    background: plan.highlight
                      ? `linear-gradient(135deg, ${plan.color}, #7C3AED)`
                      : plan.color,
                    boxShadow: plan.highlight ? `0 8px 24px ${plan.color}40` : 'none',
                    opacity: loading === plan.id ? 0.7 : 1,
                  }}
                  onClick={() => handlePurchase(plan.id)}
                  disabled={!!loading}
                >
                  {loading === plan.id ? (
                    <span>Processing...</span>
                  ) : (
                    <>
                      {(
                        <><Star size={16} /> Subscribe — {plan.priceDisplay}</>
                      )}
                    </>
                  )}
                </button>
              )}

              {plan.id !== 'FREE' && (
                <p style={styles.paymentNote}>
                  💳 GPay / PhonePe / Paytm / Card
                </p>
              )}
            </div>
          );
        })}
      </div>

      {/* Bottom trust badges */}
      <div style={styles.trustSection}>
        {[
          { emoji: '🔒', text: 'Secure UPI Payment' },
          { emoji: '❌', text: 'Cancel Anytime' },
          { emoji: '🇮🇳', text: 'Made in India' },
          { emoji: '📱', text: 'Works on WhatsApp' },
        ].map((t, i) => (
          <div key={i} style={styles.trustBadge}>
            <span>{t.emoji}</span>
            <span style={styles.trustText}>{t.text}</span>
          </div>
        ))}
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@700;800&family=DM+Sans:wght@400;500;600&display=swap');
      `}</style>
    </div>
  );
}

const styles = {
  page: { height: '100%', overflowY: 'auto', background: '#F8FAFF', fontFamily: "'DM Sans', sans-serif" },
  header: { position: 'relative', background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)', padding: '40px 20px 48px', textAlign: 'center', overflow: 'hidden' },
  headerGlow: { position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 400, height: 200, borderRadius: '50%', background: 'radial-gradient(circle, rgba(79,70,229,0.4) 0%, transparent 70%)' },
  headerContent: { position: 'relative', zIndex: 1 },
  headerTag: { display: 'inline-block', background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.9)', fontSize: 13, fontWeight: 600, padding: '5px 14px', borderRadius: 100, marginBottom: 14, border: '1px solid rgba(255,255,255,0.15)' },
  headerTitle: { fontFamily: "'Sora', sans-serif", fontSize: 28, fontWeight: 800, color: '#FFFFFF', marginBottom: 10, letterSpacing: '-0.02em' },
  headerDesc: { fontSize: 14, color: 'rgba(255,255,255,0.65)', lineHeight: 1.6 },
  plansContainer: { padding: '20px 16px', display: 'flex', flexDirection: 'column', gap: 16 },
  planCard: { background: '#FFFFFF', borderRadius: 20, border: '1px solid', padding: '20px 18px', position: 'relative', transition: 'transform 0.2s' },
  badge: { position: 'absolute', top: -13, left: '50%', transform: 'translateX(-50%)', color: '#FFFFFF', fontSize: 12, fontWeight: 800, padding: '4px 16px', borderRadius: 100, whiteSpace: 'nowrap', background: '#F59E0B', boxShadow: '0 2px 8px rgba(245,158,11,0.4)' },
  planTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18, paddingTop: 8 },
  planName: { fontFamily: "'Sora', sans-serif", fontSize: 18, fontWeight: 800, marginBottom: 3, letterSpacing: '-0.01em' },
  planTagline: { fontSize: 12, color: '#94A3B8', fontWeight: 500 },
  priceBlock: { textAlign: 'right' },
  priceMain: { fontFamily: "'Sora', sans-serif", fontSize: 30, fontWeight: 900, lineHeight: 1, letterSpacing: '-0.03em' },
  pricePeriod: { fontSize: 12, color: '#94A3B8', marginTop: 2 },
  perDay: { fontSize: 11, color: '#10B981', fontWeight: 600, marginTop: 3, background: '#ECFDF5', padding: '2px 6px', borderRadius: 100, display: 'inline-block' },
  featureList: { display: 'flex', flexDirection: 'column', gap: 9, marginBottom: 20 },
  featureRow: { display: 'flex', alignItems: 'center', gap: 10 },
  featureCheck: { width: 20, height: 20, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, flexShrink: 0 },
  featureText: { fontSize: 13, lineHeight: 1.4 },
  ctaBtn: { width: '100%', border: 'none', borderRadius: 12, padding: '14px', fontSize: 15, fontWeight: 700, cursor: 'pointer', color: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontFamily: "'DM Sans', sans-serif", transition: 'opacity 0.2s' },
  currentPlanBtn: { width: '100%', padding: '12px', textAlign: 'center', fontSize: 14, fontWeight: 600, color: '#10B981', background: '#ECFDF5', borderRadius: 12, border: '1px solid #A7F3D0' },
  paymentNote: { textAlign: 'center', fontSize: 11, color: '#94A3B8', marginTop: 10 },
  trustSection: { display: 'flex', justifyContent: 'center', gap: 16, flexWrap: 'wrap', padding: '20px 16px 40px' },
  trustBadge: { display: 'flex', alignItems: 'center', gap: 6, background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 100, padding: '8px 14px' },
  trustText: { fontSize: 12, fontWeight: 600, color: '#475569' },
};
